import { useState } from "react";

/**
 * Hook para manejar formularios.
 * 
 * @param {object} initialState - The initial state of the form.
 * @param {object} validations - An object with field names as keys and objects as values. The objects must have a
 *   `test` property with a function that takes the value of the field as an argument and returns a boolean indicating
 *   whether the value is valid or not, and a `message` property with the error message to display if the value is
 *   invalid.
 * @returns {object} - An object with the following properties:
 *   - `formData`: The current state of the form.
 *   - `errors`: An object with the same keys as `validations` and the error messages as values.
 *   - `handleChange`: A function to call when a form field changes, it takes the event as an argument.
 *   - `handleSubmit`: A function to call when the form is submitted, it takes the event and a callback as arguments.
 */
const useForm = (initialState, validations) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  // Validar un campo del formulario
  const validateField = (name, value) => {
    if (validations[name]) {      
      return validations[name].test(value) ? "" : validations[name].message;
    }
    return "";
  };

  // Manejar cambios en inputs de texto y archivos
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;    

    if (type === "file") {            
      handleFileChange(name, files);
    } else {
      const error = validateField(name, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));

      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  // Manejar selección de archivos
  const handleFileChange = (name, files) => {
    if (files && files.length > 0) {      
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: files[0], // Guardamos el archivo seleccionado
      }));      
    }
  };

  // Manejar el envío del formulario
  const handleSubmit = (e, callback) => {
    e.preventDefault();

    const validationErrors = {};
    let isValid = true;

    for (const field in validations) {
      const error = validateField(field, formData[field] || "");
      if (error) {
        isValid = false;
        validationErrors[field] = error;
      }
    }

    setErrors(validationErrors);

    if (isValid && callback) {
      callback(formData);
    }
  };

  // Resetear el formulario
  const resetForm = () => {
    setFormData(initialState);
    setErrors({});
  };

  return {
    formData,
    errors,
    handleChange,
    handleFileChange,
    handleSubmit,
    resetForm,
    setFormData,
  };
};

export default useForm;
